import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signInWithPopup,
    signOut,
    updateProfile,
    sendPasswordResetEmail,
    confirmPasswordReset,
    verifyPasswordResetCode,
    updatePassword,
    reauthenticateWithCredential,
    EmailAuthProvider
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase/config';
import toast from 'react-hot-toast';

export const authService = {

    async register(email, password, userData) {
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            await updateProfile(user, {
                displayName: `${userData.firstName} ${userData.lastName}`
            });

            const userDoc = {
                uid: user.uid,
                email: user.email,
                firstName: userData.firstName,
                lastName: userData.lastName,
                phone: userData.phone || '',
                company: userData.company || '',
                authProvider: 'email',
                emailVerified: user.emailVerified,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                lastSignIn: new Date().toISOString(),
                profileComplete: true
            };

            await setDoc(doc(db, 'users', user.uid), userDoc);

            await setDoc(doc(db, 'cart', user.uid), {
                items: [],
                total: 0,
                updatedAt: new Date().toISOString()
            });

            toast.success('Account created successfully!');
            return { user, error: null };
        } catch (error) {
            const errorMessage = this.getErrorMessage(error.code);
            toast.error(errorMessage);
            return { user: null, error: errorMessage };
        }
    },

    async login(email, password) {
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            await updateDoc(doc(db, 'users', user.uid), {
                lastSignIn: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });

            toast.success('Welcome back!');
            return { user, error: null };
        } catch (error) {
            const errorMessage = this.getErrorMessage(error.code);
            toast.error(errorMessage);
            return { user: null, error: errorMessage };
        }
    },

    async signInWithGoogle() {
        try {
            const result = await signInWithPopup(auth, googleProvider);
            const user = result.user;

            const userDoc = await getDoc(doc(db, 'users', user.uid));

            if (!userDoc.exists()) {
                const [firstName, ...lastNameParts] = (user.displayName || 'User').split(' ');
                const lastName = lastNameParts.join(' ') || '';

                const newUserDoc = {
                    uid: user.uid,
                    email: user.email,
                    firstName: firstName,
                    lastName: lastName,
                    phone: '',
                    company: '',
                    authProvider: 'google',
                    profilePicture: user.photoURL || '',
                    emailVerified: user.emailVerified,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    lastSignIn: new Date().toISOString(),
                    profileComplete: true
                };

                await setDoc(doc(db, 'users', user.uid), newUserDoc);

                await setDoc(doc(db, 'cart', user.uid), {
                    items: [],
                    total: 0,
                    updatedAt: new Date().toISOString()
                });

                toast.success('Welcome to AM CARDMATE!');
            } else {
                await updateDoc(doc(db, 'users', user.uid), {
                    lastSignIn: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                });

                toast.success('Welcome back!');
            }

            return { user, error: null };
        } catch (error) {
            if (error.code === 'auth/popup-closed-by-user') {
                return { user: null, error: null };
            }

            const errorMessage = this.getErrorMessage(error.code);
            toast.error(errorMessage);
            return { user: null, error: errorMessage };
        }
    },

    async logout() {
        try {
            await signOut(auth);
            toast.success('Logged out successfully');
        } catch (error) {
            toast.error('Error logging out');
        }
    },

    async resetPassword(email) {
        try {
            await sendPasswordResetEmail(auth, email, {
                url: `${window.location.origin}/login`,
                handleCodeInApp: false
            });
            toast.success('Password reset email sent! Please check your inbox.');
            return { error: null };
        } catch (error) {
            const errorMessage = this.getErrorMessage(error.code);
            toast.error(errorMessage);
            return { error: errorMessage };
        }
    },

    async verifyPasswordResetCode(oobCode) {
        try {
            const email = await verifyPasswordResetCode(auth, oobCode);
            return { email, error: null };
        } catch (error) {
            const errorMessage = this.getErrorMessage(error.code);
            return { email: null, error: errorMessage };
        }
    },

    async confirmPasswordReset(oobCode, newPassword) {
        try {
            await confirmPasswordReset(auth, oobCode, newPassword);
            toast.success('Password reset successfully! You can now sign in with your new password.');
            return { error: null };
        } catch (error) {
            const errorMessage = this.getErrorMessage(error.code);
            toast.error(errorMessage);
            return { error: errorMessage };
        }
    },

    async changePassword(currentPassword, newPassword) {
        try {
            const user = auth.currentUser;
            if (!user) {
                throw new Error('No user logged in');
            }

            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential);

            await updatePassword(user, newPassword);

            toast.success('Password updated successfully!');
            return { error: null };
        } catch (error) {
            const errorMessage = this.getErrorMessage(error.code);
            toast.error(errorMessage);
            return { error: errorMessage };
        }
    },

    async getUserProfile(uid) {
        try {
            const docRef = doc(db, 'users', uid);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                return { data: docSnap.data(), error: null };
            } else {
                return { data: null, error: 'User profile not found' };
            }
        } catch (error) {
            return { data: null, error: error.message };
        }
    },

    async createBasicProfile(user) {
        try {
            const [firstName, ...lastNameParts] = (user.displayName || 'User').split(' ');
            const lastName = lastNameParts.join(' ') || '';

            const basicProfile = {
                uid: user.uid,
                email: user.email,
                firstName: firstName,
                lastName: lastName,
                phone: '',
                company: '',
                authProvider: user.providerData[0]?.providerId === 'google.com' ? 'google' : 'email',
                profilePicture: user.photoURL || '',
                emailVerified: user.emailVerified,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                profileComplete: false
            };

            await setDoc(doc(db, 'users', user.uid), basicProfile);
            return { data: basicProfile, error: null };
        } catch (error) {
            return { data: null, error: error.message };
        }
    },

    async updateUserProfile(uid, userData) {
        try {
            const updateData = {
                ...userData,
                updatedAt: new Date().toISOString(),
                profileComplete: true
            };

            await updateDoc(doc(db, 'users', uid), updateData);
            return { error: null };
        } catch (error) {
            return { error: error.message };
        }
    },

    getErrorMessage(errorCode) {
        switch (errorCode) {
            case 'auth/user-disabled':
                return 'This account has been disabled. Please contact support.';
            case 'auth/user-not-found':
                return 'No account found with this email address.';
            case 'auth/wrong-password':
                return 'Incorrect password. Please try again.';
            case 'auth/email-already-in-use':
                return 'An account with this email already exists.';
            case 'auth/weak-password':
                return 'Password should be at least 6 characters long.';
            case 'auth/invalid-email':
                return 'Please enter a valid email address.';
            case 'auth/operation-not-allowed':
                return 'This sign-in method is not enabled. Please contact support.';
            case 'auth/account-exists-with-different-credential':
                return 'An account already exists with the same email but different sign-in credentials.';
            case 'auth/auth-domain-config-required':
                return 'Authentication configuration error. Please contact support.';
            case 'auth/cancelled-popup-request':
                return 'Sign-in was cancelled. Please try again.';
            case 'auth/popup-blocked':
                return 'Sign-in popup was blocked by your browser. Please allow popups and try again.';
            case 'auth/popup-closed-by-user':
                return 'Sign-in was cancelled.';
            case 'auth/unauthorized-domain':
                return 'This domain is not authorized for authentication.';
            case 'auth/too-many-requests':
                return 'Too many failed attempts. Please try again later.';
            case 'auth/network-request-failed':
                return 'Network error. Please check your connection and try again.';
            case 'auth/requires-recent-login':
                return 'Please log in again to perform this action.';
            case 'auth/invalid-action-code':
                return 'The action code is invalid. This can happen if the code is malformed, expired, or has already been used.';
            case 'auth/expired-action-code':
                return 'The action code has expired. Please request a new password reset.';
            case 'auth/invalid-continue-url':
                return 'The continue URL provided is invalid.';
            case 'auth/missing-continue-url':
                return 'A continue URL must be provided in the request.';
            default:
                return 'An error occurred. Please try again.';
        }
    },

    validateEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    },

    validatePassword(password) {
        if (password.length < 6) {
            return 'Password must be at least 6 characters long';
        }
        if (password.length > 128) {
            return 'Password must be less than 128 characters';
        }
        return null;
    },

    isOnline() {
        return navigator.onLine;
    }
};